import { NATIVE_TOKEN_ADDRESS } from '@sirius-cybernetics-42/product-config'
import {
  buildOrderedPairMarketRefs,
  getTokenLegRole,
  orderPairLegsForDisplay,
} from '@sirius-cybernetics-42/product-config'

import { isNativeCurrency } from '@/config/native'
import { getBaseQuoteSpotRate, type OnChainPairData } from '@/features/market/useOnChainPairData'
import {
  addressesEquivalent,
  buildDisplayTokenRef,
  formatPairLabel,
  formatPoolId,
} from '@/data/marketApi/normalizeMarketToken'
import type { PoolIdentity, PoolSummary } from '@/data/marketApi/types'
import { ChainId, Currency, Pair, Token } from '@/sdk'
import { currencyId } from '@/utils/currencyId'
import { buildOffCatalogPoolDetailPath } from '@/config/pairRouteParams'
import { buildPoolDetailPath } from '@/config/liquidityPools'
import { wrappedCurrency } from '@/utils/wrappedCurrency'

import { computePoolValueSharePct } from './poolReserveShares'

export type PoolProfileSource = 'api' | 'on-chain' | 'synthetic' | 'none'

/**
 * Resolución de pools: la Market API (`GET /v1/pools`) es la fuente normativa cuando el par
 * está indexado. `findPoolForCurrencies` empareja por **dirección de contrato**, nunca solo
 * por símbolo, para no mezclar pools distintas con el mismo ticker.
 *
 * Si el par no está en el catálogo indexado, `synthesizePoolSummaryFromPair` construye un
 * perfil mínimo leyendo el contrato on-chain. Esa síntesis es intencional: evita reutilizar
 * stats de otra pool del listado que comparta símbolos pero no los mismos contratos.
 */

function currencyAddressForPoolMatch(currency: Currency, chainId: ChainId): string {
  if (isNativeCurrency(currency, chainId)) return NATIVE_TOKEN_ADDRESS
  if (currency instanceof Token) return currency.address
  return ''
}

export function sortWrappedTokensOnChain(wrappedA: Token, wrappedB: Token): [Token, Token] {
  return wrappedA.sortsBefore(wrappedB) ? [wrappedA, wrappedB] : [wrappedB, wrappedA]
}

/** Orden on-chain V2 (reservas/contrato). */
export function sortCurrencyPair(
  currencyA: Currency,
  currencyB: Currency,
  chainId: ChainId,
): [Currency, Currency, Token, Token] {
  const wrappedA = wrappedCurrency(currencyA, chainId)
  const wrappedB = wrappedCurrency(currencyB, chainId)
  if (!wrappedA || !wrappedB) {
    throw new Error('[poolProfile] monedas inválidas para el par')
  }
  const [w0, w1] = sortWrappedTokensOnChain(wrappedA, wrappedB)
  if (w0 === wrappedA) {
    return [currencyA, currencyB, wrappedA, wrappedB]
  }
  return [currencyB, currencyA, wrappedB, wrappedA]
}

/** Orden de visualización (base/quote) según reglas de producto. */
export function sortCurrenciesForDisplay(
  currencyA: Currency,
  currencyB: Currency,
  chainId: ChainId,
): [Currency, Currency] {
  const wrappedA = wrappedCurrency(currencyA, chainId)
  const wrappedB = wrappedCurrency(currencyB, chainId)
  if (!wrappedA || !wrappedB) {
    throw new Error('[poolProfile] monedas inválidas para el par')
  }

  const legA = {
    address: isNativeCurrency(currencyA, chainId) ? NATIVE_TOKEN_ADDRESS : wrappedA.address,
    symbol: currencyA.symbol ?? '?',
    role: getTokenLegRole(
      isNativeCurrency(currencyA, chainId) ? NATIVE_TOKEN_ADDRESS : wrappedA.address,
      chainId,
    ),
  }
  const legB = {
    address: isNativeCurrency(currencyB, chainId) ? NATIVE_TOKEN_ADDRESS : wrappedB.address,
    symbol: currencyB.symbol ?? '?',
    role: getTokenLegRole(
      isNativeCurrency(currencyB, chainId) ? NATIVE_TOKEN_ADDRESS : wrappedB.address,
      chainId,
    ),
  }

  const [base] = orderPairLegsForDisplay(legA, legB, chainId)
  const baseIsA = base.address.toLowerCase() === legA.address.toLowerCase()
  return baseIsA ? [currencyA, currencyB] : [currencyB, currencyA]
}

export function findPoolForCurrencies<T extends PoolSummary | PoolIdentity>(
  pools: readonly T[] | undefined,
  currencyA: Currency,
  currencyB: Currency,
  chainId: ChainId,
): T | undefined {
  if (!pools?.length) return undefined
  const addrA = currencyAddressForPoolMatch(currencyA, chainId)
  const addrB = currencyAddressForPoolMatch(currencyB, chainId)
  return pools.find((pool) => {
    const t0 = pool.token0.address
    const t1 = pool.token1.address
    return (
      (addressesEquivalent(t0, addrA, chainId) && addressesEquivalent(t1, addrB, chainId)) ||
      (addressesEquivalent(t0, addrB, chainId) && addressesEquivalent(t1, addrA, chainId))
    )
  })
}

function tokenRefFromCurrency(currency: Currency, wrapped: Token, chainId: ChainId) {
  const symbol = currency.symbol ?? '?'
  return buildDisplayTokenRef(wrapped.address, symbol, chainId, symbol)
}

export function computePoolIdFromCurrencies(
  currencyA: Currency,
  currencyB: Currency,
  chainId: ChainId,
): string {
  const [c0, c1] = sortCurrenciesForDisplay(currencyA, currencyB, chainId)
  const w0 = wrappedCurrency(c0, chainId)
  const w1 = wrappedCurrency(c1, chainId)
  if (!w0 || !w1) {
    throw new Error('[poolProfile] monedas inválidas para el par')
  }
  const ref0 = tokenRefFromCurrency(c0, w0, chainId)
  const ref1 = tokenRefFromCurrency(c1, w1, chainId)
  return formatPoolId(ref0.address, ref0.symbol, ref1.address, ref1.symbol, chainId)
}

function formatReserveAmount(amount: string, symbol: string): string {
  return `${amount} ${symbol}`
}

function sharePctFromReserves(
  reserves0: string,
  reserves1: string,
  price: string,
): [string | undefined, string | undefined] {
  const shares = computePoolValueSharePct(reserves0, reserves1, price)
  if (!shares) return [undefined, undefined]
  return [shares.share0, shares.share1]
}

/**
 * Perfil off-catalog: solo se usa cuando el par no aparece en `GET /v1/pools`.
 * No inventa TVL, volumen ni APY; los campos ausentes quedan en `'—'`.
 */
export function synthesizePoolSummaryFromPair(
  currencyA: Currency,
  currencyB: Currency,
  chainId: ChainId,
  pairData: OnChainPairData,
  exists: boolean,
): PoolSummary {
  const [onChainC0, onChainC1, w0, w1] = sortCurrencyPair(currencyA, currencyB, chainId)
  const ref0 = tokenRefFromCurrency(onChainC0, w0, chainId)
  const ref1 = tokenRefFromCurrency(onChainC1, w1, chainId)
  const ordered = buildOrderedPairMarketRefs(
    ref0.address,
    ref0.symbol,
    ref0.symbol,
    ref1.address,
    ref1.symbol,
    ref1.symbol,
    chainId,
  )
  const id = formatPoolId(ref0.address, ref0.symbol, ref1.address, ref1.symbol, chainId)
  const pair = formatPairLabel(ref0.address, ref0.symbol, ref1.address, ref1.symbol, chainId)
  const pairAddress = pairData.pairAddress ?? Pair.getAddress(w0, w1)
  const spotRate = exists
    ? getBaseQuoteSpotRate(
        pairData,
        { address: ordered.base.address, symbol: ordered.base.symbol },
        { address: ordered.quote.address, symbol: ordered.quote.symbol },
        chainId,
      )
    : null
  const price = spotRate?.amount ?? '—'
  const reserves0 = exists ? formatReserveAmount(pairData.reserve0, ref0.symbol) : '—'
  const reserves1 = exists ? formatReserveAmount(pairData.reserve1, ref1.symbol) : '—'
  const [share0, share1] = sharePctFromReserves(reserves0, reserves1, price)

  return {
    id,
    pair,
    pairAddress,
    token0: {
      address: ref0.address,
      symbol: ref0.symbol,
      role: ref0.role,
    },
    token1: {
      address: ref1.address,
      symbol: ref1.symbol,
      role: ref1.role,
    },
    baseToken: {
      address: ordered.base.address,
      symbol: ordered.base.symbol,
      role: ordered.base.role,
    },
    quoteToken: {
      address: ordered.quote.address,
      symbol: ordered.quote.symbol,
      role: ordered.quote.role,
    },
    price,
    change24h: '—',
    tvl: '—',
    tvlUsd: 0,
    volume24h: '—',
    fee: pairData.feeTier || '0.30%',
    reserves0,
    reserves1,
    apy: '—',
    token0SharePct: share0,
    token1SharePct: share1,
    lpTokenSymbol: `${ref0.symbol}-${ref1.symbol}`,
  }
}

export function buildPoolDetailLink(
  currencyA: Currency,
  currencyB: Currency,
  chainId: ChainId,
  pools?: readonly (PoolSummary | PoolIdentity)[],
): string {
  const apiPool = findPoolForCurrencies(pools, currencyA, currencyB, chainId)
  if (apiPool) {
    return buildPoolDetailPath(apiPool.id)
  }
  const [c0, c1] = sortCurrenciesForDisplay(currencyA, currencyB, chainId)
  return buildOffCatalogPoolDetailPath(currencyId(c0, chainId), currencyId(c1, chainId))
}

export function buildAddLiquidityDeepLink(
  currencyA: Currency,
  currencyB: Currency,
  chainId: ChainId,
): string {
  return `/add/${currencyId(currencyA, chainId)}/${currencyId(currencyB, chainId)}`
}
